import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";

interface Notification {
  id: number;
  type: string;
  message: string;
  time: string;
  read: boolean;
}

interface CurrentUser {
  id: string;
  name: string;
  email: string;
  role: string;
  avatar: string;
}

interface FeatureFlags {
  darkMode: boolean;
  globalSearch: boolean;
  notifications: boolean;
  advancedFilters: boolean;
}

interface GlobalConfig {
  environment: string;
  currentUser: CurrentUser;
  selectedWorkspace: null | Record<string, any>;
  featureFlags: FeatureFlags;
}

interface AppContextType {
  globalConfig: GlobalConfig;
  updateConfig: (updates: Partial<GlobalConfig>) => void;
  notifications: Notification[];
  setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>;
  markNotificationRead: (id: number) => void;
  unreadCount: number;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  // Initial currentUser is empty until auth finishes restoring session; useEffect below syncs when user is available.
  const [globalConfig, setGlobalConfig] = useState<GlobalConfig>({
    environment: "PROD",
    currentUser: {
      id: user?.id ?? "",
      name: user?.name ?? "",
      email: user?.email ?? "",
      role: user?.role ?? "",
      avatar: user?.avatar ?? "",
    },
    selectedWorkspace: null,
    featureFlags: {
      darkMode: true,
      globalSearch: true,
      notifications: true,
      advancedFilters: true,
    },
  });

  useEffect(() => {
    if (!user) return;
    setGlobalConfig((prev) => ({
      ...prev,
      currentUser: {
        id: user.id,
        name: user.name,
        email: user.email,
        role: user.role,
        avatar: user.avatar ?? "",
      },
    }));
  }, [user]);

  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: 1,
      type: "info",
      message: "New seller registration pending approval",
      time: "5m ago",
      read: false,
    },
    {
      id: 2,
      type: "warning",
      message: "Order #45678 requires attention",
      time: "15m ago",
      read: false,
    },
    {
      id: 3,
      type: "success",
      message: "Catalog sync completed successfully",
      time: "1h ago",
      read: true,
    },
  ]);

  const updateConfig = (updates: Partial<GlobalConfig>) => {
    setGlobalConfig((prev) => ({ ...prev, ...updates }));
  };

  const markNotificationRead = (id: number) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
    );
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <AppContext.Provider
      value={{
        globalConfig,
        updateConfig,
        notifications,
        setNotifications,
        markNotificationRead,
        unreadCount,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
};
